import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { companySetupApi } from "@/apis/custom/companySetup";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { ValidationErrorAlert } from "@/components/ui/validation-error-alert";
import { useGetAllCompanies } from "@/queries/companies/get-all";
import { useUsers } from "@/queries/users/get-all";
import { zodResolver } from "@hookform/resolvers/zod";

import { StepIndicator } from "@/components/shared/steppers/step-indicator";

import { BasicData } from "@/features/company-setup/create/basic-data";
import {
  type Annotation,
  CompanyDescription,
} from "@/features/company-setup/create/company-description";
import { DataImportStep } from "@/features/company-setup/create/data-import-step";
import {
  type FormValues,
  formSchema,
} from "@/features/company-setup/create/form-schema";
import { UserAccess } from "@/features/company-setup/create/user-access";
import { CheckIcon } from "lucide-react";
import SuccessBanner from "../../components/shared/banners/success-banner";

interface CreateSMBModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateSMBModal({ open, onOpenChange }: CreateSMBModalProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentStep, setCurrentStep] = useState(1);
  const [showValidationError, setShowValidationError] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [generatedDescription, setGeneratedDescription] = useState("");
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [descriptionAnnotations, setDescriptionAnnotations] = useState<
    Annotation[]
  >([]);
  const [showAnnotationsModal, setShowAnnotationsModal] = useState(false);
  const [selectedAnnotation, setSelectedAnnotation] = useState<
    Annotation | undefined
  >(undefined);
  const [abortController, setAbortController] =
    useState<AbortController | null>(null);

  const { users, loading: usersLoading } = useUsers(!open, true);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      companyName: "",
      companyDescription: "",
      accountLength: undefined,
      skr: undefined,
      vorsteuerabzug: true,
      skipDebtorsCreditorsImport: false,
      debtorsCreditorsFile: null,
      skipAccountsImport: false,
      accountsFile: null,
      hasCostCenters: false,
      costCentersFile: null,
      selectedUsers: [],
      addresses: [
        {
          addressLine1: "",
          postcode: "",
          city: "",
          addressLine2: "",
          country: "DE",
        },
      ],
      companyType: "STANDARD",
      legalForm: undefined,
    },
  });

  const { refetch } = useGetAllCompanies();

  useEffect(() => {
    if (!open) {
      setShowSuccessMessage(false);
      setCurrentStep(1);
      setSearchQuery("");
      setShowValidationError(false);
      setIsSubmitting(false);
      setGeneratedDescription("");
      setIsGeneratingDescription(false);
      setDescriptionAnnotations([]);
      setShowAnnotationsModal(false);
      setSelectedAnnotation(undefined);
      form.reset();
    }
  }, [open, form]);

  const handleStepClick = async (step: number) => {
    if (step < currentStep) {
      setCurrentStep(step);
      scrollFormToTop();
      return;
    }

    if (step === currentStep + 1) {
      await handleNextStep();
    }
  };

  const generateCompanyDescription = async () => {
    const companyName = form.getValues("companyName");
    const addresses = form.getValues("addresses");

    if (!companyName || !addresses || addresses.length === 0) {
      return;
    }

    const firstAddress = addresses[0];
    if (
      !firstAddress.addressLine1 ||
      !firstAddress.city ||
      !firstAddress.postcode ||
      !firstAddress.country
    ) {
      return;
    }

    // Clear previous state before generating new description
    setGeneratedDescription("");
    setDescriptionAnnotations([]);
    form.setValue("companyDescription", "");

    // Create new AbortController for this request
    const controller = new AbortController();
    setAbortController(controller);

    setIsGeneratingDescription(true);
    try {
      const response = await companySetupApi.generateCompanyDescription(
        {
          companyName,
          address: firstAddress,
        },
        controller.signal,
      );
      setGeneratedDescription(response.companyDescription);
      form.setValue("companyDescription", response.companyDescription);
      setDescriptionAnnotations(response.annotations || []);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        console.log("Company description generation was cancelled");
      } else {
        console.error("Error generating company description:", error);
      }
    } finally {
      setIsGeneratingDescription(false);
      setAbortController(null);
    }
  };

  const handleSkipAI = () => {
    if (abortController) {
      abortController.abort();
    }
    setIsGeneratingDescription(false);
    setAbortController(null);
  };

  const handleNextStep = async () => {
    const fields = getStepValidationFields(currentStep);

    const isValid = await form.trigger(fields);

    if (isValid) {
      setShowValidationError(false);

      if (currentStep === 3) {
        generateCompanyDescription().catch((error) => {
          console.error("Error generating company description:", error);
        });
      }

      setCurrentStep((prev) => Math.min(prev + 1, 4));
      scrollFormToTop();
    } else {
      setShowValidationError(true);
      setTimeout(() => setShowValidationError(false), 5000);
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    scrollFormToTop();
  };

  const onSubmit = async (data: FormValues) => {
    setIsSubmitting(true);
    try {
      await companySetupApi.createCompanySelfService({
        companyName: data.companyName,
        companyDescription: data.companyDescription,
        addresses: data.addresses,
        vatId: data.vatId,
        legalForm: data.legalForm,
        accountLength: Number.parseInt(data.accountLength),
        skr: data.skr,
        vorsteuerabzug: data.vorsteuerabzug,
        hasCostCenters: data.hasCostCenters,
        userIds: data.selectedUsers,
        companyType: data.companyType,
        debtors: data.debtorsCreditors?.debtors,
        creditors: data.debtorsCreditors?.creditors,
        accounts: data.accounts,
        costCenters: data.costCenters,
      });

      setShowSuccessMessage(true);
      //onOpenChange(false);
      await refetch();
    } catch (error) {
      toast.error(t("mandanten_create.error_creating_mandant"));
      console.error("Error creating mandant:", error);
      Sentry.captureException(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[1200px] w-[90vw] max-h-[90vh] flex flex-col"
        id="smb-modal-content"
      >
        <DialogHeader>
          <DialogTitle>{t("mandanten_create.modal_title")}</DialogTitle>
        </DialogHeader>

        <StepIndicator
          currentStep={currentStep}
          onStepClick={handleStepClick}
          totalSteps={4}
        />

        {showValidationError && (
          <ValidationErrorAlert
            onClose={() => setShowValidationError(false)}
            form={form}
            currentStep={currentStep}
          />
        )}

        <div className="flex-1 overflow-y-auto pr-2">
          {showSuccessMessage ? (
            <SMBSuccessBanner onClose={() => onOpenChange(false)} />
          ) : (
            <>
              <Form {...form}>
                <form
                  className="space-y-6 flex flex-col min-h-full"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                    }
                  }}
                >
                  <div className="flex-1">
                    {currentStep === 1 && <BasicData form={form} />}
                    {currentStep === 2 && <DataImportStep form={form} />}
                    {currentStep === 3 && (
                      <UserAccess
                        form={form}
                        users={users}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                      />
                    )}
                    {currentStep === 4 && (
                      <CompanyDescription
                        isGeneratingDescription={isGeneratingDescription}
                        annotations={descriptionAnnotations}
                        onAnnotationClick={(annotation) => {
                          setSelectedAnnotation(annotation);
                          setShowAnnotationsModal(true);
                        }}
                        onSkipAI={handleSkipAI}
                      />
                    )}
                  </div>

                  <div className="flex justify-end space-x-2 pt-4">
                    {currentStep > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handlePreviousStep}
                      >
                        {t("mandanten_create.back")}
                      </Button>
                    )}

                    {currentStep < 4 ? (
                      <Button type="button" onClick={handleNextStep}>
                        {t("mandanten_create.next")}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        disabled={isSubmitting}
                        onClick={async () => {
                          const isValid = await form.trigger();
                          if (!isValid) {
                            const errors = form.formState.errors;
                            console.log("Validation errors:", errors);
                            return;
                          }
                          if (isValid) {
                            const data = form.getValues();
                            await onSubmit(data);
                          }
                        }}
                      >
                        {isSubmitting
                          ? t("mandanten_create.creating")
                          : t("mandanten_create.create_mandant")}
                      </Button>
                    )}
                  </div>
                </form>
              </Form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function getStepValidationFields(step: number): Array<keyof FormValues> {
  if (step === 1) {
    return [
      "companyName",
      "addresses",
      "vatId",
      "accountLength",
      "skr",
      "vorsteuerabzug",
      "legalForm",
    ];
  }
  if (step === 2) {
    return [
      "hasCostCenters",
      "costCentersFile",
      "costCenters",
      "skipDebtorsCreditorsImport",
      "debtorsCreditorsFile",
      "skipAccountsImport",
      "accountsFile",
    ];
  } else if (step === 3) {
    return ["selectedUsers"];
  } else if (step === 4) {
    return ["companyDescription"];
  }
  return [];
}

function scrollFormToTop() {
  console.log("scrollFormToTop called");

  // Try multiple selector approaches to ensure we find the container
  const selectors = [
    "#smb-modal-content .overflow-y-auto",
    ".DialogContent .overflow-y-auto",
    "div[role='dialog'] .overflow-y-auto",
    ".overflow-y-auto",
  ];

  let formContainer: Element | null = null;

  // Try each selector until we find a matching element
  for (const selector of selectors) {
    const element = document.querySelector(selector);
    if (element) {
      formContainer = element;
      console.log(`Found container with selector: ${selector}`);
      break;
    }
  }

  if (formContainer) {
    const scrollOptions = { top: 0, behavior: "smooth" as ScrollBehavior };

    // Try multiple scroll methods
    try {
      // Method 1: scrollTo
      formContainer.scrollTo(scrollOptions);

      // Method 2: scrollTop
      formContainer.scrollTop = 0;

      // Method 3: Delayed scrolling to ensure DOM is ready
      setTimeout(() => {
        if (formContainer) {
          formContainer.scrollTo(scrollOptions);
          formContainer.scrollTop = 0;
        }
      }, 100);
    } catch (error) {
      console.error("Error while scrolling:", error);
    }
  } else {
    console.warn("Could not find any scrollable container");
  }
}
function SMBSuccessBanner({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();

  return (
    <SuccessBanner
      title={t("mandanten_create.success.title")}
      description={t("mandanten_create.success.description", {
        timeframe: t("tenant_creation_timeframe"),
      })}
      buttonText={t("mandanten_create.success.close")}
      onButtonClick={onClose}
    />
  );
}
